import * as React from "react";
import { cn } from "@/lib/utils";

export interface PriceRangeValue {
  min: number;
  max: number;
}

export interface PriceRangeProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "onChange" | "defaultValue"> {
  /** Histogram bucket boundaries (N+1 values). */
  buckets: number[];
  /** Counts per bucket (N values). Without these, the histogram does not show up. */
  counts?: number[];
  /** Axis labels (mono). */
  labels?: string[];
  /** Absolute bounds. */
  min: number;
  max: number;
  value?: PriceRangeValue;
  defaultValue?: PriceRangeValue;
  onApply?: (range: PriceRangeValue) => void;
  applyLabel?: string;
  /** Currency prefix and locale. Defaults to BRL, pt-BR. */
  currencyPrefix?: string;
  locale?: string;
}

const digits = (s: string) => Number(s.replace(/\D/g, "") || 0);

/**
 * The currency prefix is drawn by `::before`, as the reference does (`.pf::before { content: "R$" }`):
 * it decorates a field that already names itself, so it is not text of the document.
 */
const PREFIXED_FIELD_CLASS =
  "relative flex-1 before:pointer-events-none before:absolute before:left-[9px] before:top-1/2 before:-translate-y-1/2 before:text-xs before:text-[rgb(61_63_57_/_0.55)] before:content-[attr(data-prefix)]";

export const PriceRange = React.forwardRef<HTMLDivElement, PriceRangeProps>(
  (
    {
      className,
      buckets,
      counts,
      labels,
      min,
      max,
      value,
      defaultValue,
      onApply,
      applyLabel = "Aplicar",
      currencyPrefix = "R$",
      locale = "pt-BR",
      ...props
    },
    ref,
  ) => {
    const isControlled = value !== undefined;
    const [internal, setInternal] = React.useState<PriceRangeValue>(
      defaultValue ?? { min, max },
    );
    const range = isControlled ? value : internal;
    const setRange = (r: PriceRangeValue) => {
      if (!isControlled) setInternal(r);
    };

    const fmt = (n: number) => n.toLocaleString(locale);
    const maxCount = counts && counts.length ? Math.max(...counts) : 1;

    return (
      <div ref={ref} className={cn("w-[280px]", className)} {...props}>
        {counts && counts.length > 0 && (
          <>
            <div className="mb-1 flex h-16 items-end gap-[3px]">
              {counts.map((c, i) => {
                const lo = buckets[i];
                const hi = buckets[i + 1];
                const active = hi > range.min && lo < range.max;
                return (
                  <button
                    key={i}
                    type="button"
                    aria-label={`${currencyPrefix} ${fmt(lo)} – ${fmt(hi)}`}
                    onClick={() => setRange({ min: lo, max: hi })}
                    style={{ height: `${Math.max(5, (c / maxCount) * 100)}%` }}
                    className={cn(
                      "min-h-[3px] flex-1 cursor-pointer rounded-t-[2px] transition-colors hover:bg-tb-green",
                      active ? "bg-tb-green" : "bg-tb-porcelain",
                    )}
                  />
                );
              })}
            </div>
            {/* The reference's axis: 11px mono at 55% ink. */}
            {labels && (
              <div className="mb-3 flex justify-between font-mono text-[11px] text-[rgb(61_63_57_/_0.55)]">
                {labels.map((l, i) => (
                  <span key={i}>{l}</span>
                ))}
              </div>
            )}
          </>
        )}

        <form
          onSubmit={(e) => {
            e.preventDefault();
            onApply?.(range);
          }}
        >
          <div className="mb-3 flex items-center gap-2">
            <label data-prefix={currencyPrefix} className={PREFIXED_FIELD_CLASS}>
              <input
                inputMode="numeric"
                aria-label="Preço mínimo"
                value={fmt(range.min)}
                onChange={(e) => setRange({ ...range, min: digits(e.target.value) })}
                className="h-10 w-full min-w-0 rounded-[var(--tb-radius-xs)] border border-[var(--tb-border)] bg-tb-white py-2 pl-[30px] pr-2.5 font-sans text-[13px] text-[var(--fg)] outline-none focus:border-tb-green"
              />
            </label>
            {/* An em dash in sans at 13px, as the reference writes it. */}
            <span className="font-sans text-[13px] text-[rgb(61_63_57_/_0.55)]">—</span>
            <label data-prefix={currencyPrefix} className={PREFIXED_FIELD_CLASS}>
              <input
                inputMode="numeric"
                aria-label="Preço máximo"
                value={fmt(range.max)}
                onChange={(e) => setRange({ ...range, max: digits(e.target.value) })}
                className="h-10 w-full min-w-0 rounded-[var(--tb-radius-xs)] border border-[var(--tb-border)] bg-tb-white py-2 pl-[30px] pr-2.5 font-sans text-[13px] text-[var(--fg)] outline-none focus:border-tb-green"
              />
            </label>
          </div>
          <button
            type="submit"
            className="w-full cursor-pointer rounded-[var(--tb-radius-xs)] bg-tb-green p-3 text-xs font-bold uppercase leading-[normal] tracking-[0.14em] text-tb-porcelain transition-colors hover:bg-[var(--tb-green-deep)]"
          >
            {applyLabel}
          </button>
        </form>
      </div>
    );
  },
);
PriceRange.displayName = "PriceRange";
